import { Component, NgZone, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { AuthService } from 'src/app/store/auth';
import { GlobalNavBarComponent } from './layout/global-nav-bar/global-nav-bar.component';
import { BottomSheetComponent, ActionDialogComponent } from './ui-kit';
import { NgTemplateOutlet } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { NavigationState } from 'src/app/store/navigation/navigation.state';
import { DeviceState } from './store/device/device.state';
import { CommandPaletteComponent } from './layout/command-palette/command-palette.component';
import { ResponsiveModalBackdropComponent } from './ui-kit/responsive-modal/responsive-modal-backdrop.component';
import { CommandPaletteService } from './store/command-palette';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { UserInitService } from 'src/app/core/services/user-init.service';
import { environment } from 'src/environments/environment';
/**
 * Root App Component
 *
 * Handles application initialization, including:
 * - UI state initialization (theme, preferences)
 * - Auth state initialization (restore session)
 * - Signal-based states (AccountState auto-syncs with Firebase Auth)
 * - Initial navigation for authenticated users
 * - Navigation bars visibility management
 * - Global bottom sheet management
 * - Global backdrop management
 */
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    NgTemplateOutlet,
    IonApp,
    IonRouterOutlet,
    GlobalNavBarComponent,
    BottomSheetComponent,
    ActionDialogComponent,
    CommandPaletteComponent,
    ResponsiveModalBackdropComponent,
  ],
  animations: [
    trigger('backdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly deviceState = inject(DeviceState);
  private readonly zone = inject(NgZone);
  private readonly userInitService = inject(UserInitService);

  /** Navigation state (replaces NavigationBarsState + NavigationBarsService) */
  readonly nav = inject(NavigationState);

  /** Command palette service */
  readonly commandPaletteService = inject(CommandPaletteService);

  /** Hide bottom global nav on routes that set `data.hideGlobalNavBar` (e.g. live workshop). */
  readonly hideGlobalNavBar = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.leafRouteData()['hideGlobalNavBar'] === true),
      startWith(this.leafRouteData()['hideGlobalNavBar'] === true),
    ),
    { initialValue: this.leafRouteData()['hideGlobalNavBar'] === true },
  );

  // Navigation bar visibility (signal reads)
  readonly bottomNavBarVisible = this.nav.bottomNavVisible;

  // Action dialog (signal reads)
  readonly actionDialog = this.nav.actionDialog;
  readonly actionDialogVisible = this.nav.actionDialogVisible;

  // Backdrop (signal read)
  readonly backdrop = this.nav.backdrop;

  // Safety banner — visible only when the app is wired to local emulators.
  readonly emulatorModeActive = environment.emulator === true;
  readonly emulatorProjectId = environment.firebase.projectId;

  constructor() {
    // Initialize apple app site association
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        // Example url: https://blumemethod.app/tabs/tab2
        const slug = event.url.split(".app").pop();
        if (slug) {
          this.router.navigateByUrl(slug);
        }
        // If no match, do nothing - let regular routing
        // logic take over
      });
    });
  }

  ngOnInit(): void {
    // Reset init flag so users go through /loading on every page refresh
    this.userInitService.reset();
    // Initialize Auth state - loads saved session and sets up Firebase listener
    this.authService.initialize();
  }

  closeActionDialog(): void {
    this.nav.closeActionDialog();
  }

  onBackdropClick(): void {
    this.nav.hideBackdrop();
  }

  closeCommandPalette(): void {
    this.commandPaletteService.close();
  }

  onCommandPaletteSearchChange(searchValue: string): void {
    this.commandPaletteService.setSearchValue(searchValue);
  }

  private leafRouteData(): Record<string, unknown> {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.data as Record<string, unknown>;
  }
}
